import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

export interface MFAFactor {
    id: string;
    kind: "totp" | "webauthn" | "recovery_code";
    label: string;
    lastUsedAt?: string;
    createdAt: string;
}

const FACTORS_KEY = ["auth", "mfa", "factors"] as const;

export function useMFAFactors() {
    return useQuery({
        queryKey: FACTORS_KEY,
        queryFn: () => api.get<{ factors: MFAFactor[] }>("/auth/mfa/factors").then((d) => d.factors),
        staleTime: 30_000,
    });
}

export interface TOTPEnrollment {
    secret: string;
    otpauthUrl: string;
    issuer: string;
    account: string;
}

export function useStartTOTPEnroll() {
    return useMutation({
        mutationFn: () => api.post<TOTPEnrollment>("/auth/mfa/totp/enroll/start"),
    });
}

interface VerifyTOTPInput {
    secret: string;
    code: string;
    label?: string;
}

export function useVerifyTOTPEnroll() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: VerifyTOTPInput) =>
            api.post<{ factor: MFAFactor }>("/auth/mfa/totp/enroll/verify", input),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: FACTORS_KEY });
        },
    });
}

export function useDeleteMFAFactor() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => api.delete<void>(`/auth/mfa/factors/${id}`),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: FACTORS_KEY });
        },
    });
}

interface SubmitMFAInput {
    challengeToken: string;
    code: string;
}

import { ME_QUERY_KEY, type CurrentUser } from "./auth";

export function useSubmitMFA() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input: SubmitMFAInput) =>
            api.post<CurrentUser>("/auth/mfa/totp", input),
        onSuccess: (user) => {
            qc.setQueryData(ME_QUERY_KEY, user);
        },
    });
}
